/* oxlint-disable max-lines */
import { definePreset } from '@primevue/themes';
import Lara from '@primevue/themes/lara';

// Connect PrimeVue's Lara theme to our design tokens.

const ramp = (name) =>
  Object.fromEntries(
    [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950].map((s) => [s, `var(--color-${name}-${s})`]),
  );

// Use Tailwind spacing values.
const space = (n) => `calc(var(--spacing) * ${n})`;

// Toast severities, each with the colour of its left bar and icon.
const TOAST_SEVERITIES = [
  { severity: 'info', color: 'var(--color-info-600)' },
  { severity: 'success', color: 'var(--color-success-600)' },
  { severity: 'warn', color: 'var(--color-warning-600)' },
  { severity: 'error', color: 'var(--color-danger-600)' },
];

// A white card with neutral text; only the left bar and the icon carry the severity colour.
const toastSeverities = (scheme) =>
  Object.fromEntries(
    TOAST_SEVERITIES.map(({ severity, color }) => [
      severity,
      {
        background: '{app.card}',
        borderColor: color,
        color: '{app.content}',
        detailColor: '{app.contentMuted}',
        closeButton: { hoverBackground: scheme === 'dark' ? '{surface.800}' : '{surface.100}' },
      },
    ]),
  );

export default definePreset(Lara, {
  primitive: {
    // Map PrimeVue's four radius sizes to our two sizes. Lara still needs every key.
    borderRadius: {
      sm: 'var(--radius-sm)',
      md: 'var(--radius-sm)',
      lg: 'var(--radius-lg)',
      xl: 'var(--radius-lg)',
      full: 'var(--radius-full)',
    },
    green: ramp('success'),
    red: ramp('danger'),
    sky: ramp('info'),
    blue: ramp('info'),
    orange: ramp('warning'),
    yellow: ramp('warning'),
  },
  semantic: {
    primary: ramp('primary'),
    // A thin tenant-coloured outline. Lara focuses with a 0.2rem halo, which reads as a shadow.
    // The runtime theme raises the colour to 3:1 contrast so it stays visible for pale brands.
    focusRing: {
      width: '1px',
      style: 'solid',
      color: 'var(--color-focus-ring)',
      offset: '0',
    },
    // Give menus and option lists the same spacing.
    list: {
      padding: space(1),
      gap: space(0.5),
      option: { padding: `${space(2)} ${space(2.5)}`, borderRadius: 'var(--radius-md)' },
    },
    navigation: {
      list: { padding: space(1), gap: space(0.5) },
      item: { padding: `${space(2)} ${space(2.5)}`, borderRadius: 'var(--radius-md)' },
    },
    formField: {
      sm: { fontSize: 'var(--text-sm)' },
      lg: { fontSize: 'var(--text-lg)' },
    },
    // PrimeVue exports the `app` values as CSS variables for Tailwind.
    colorScheme: {
      light: {
        // Readable text on the tenant colour.
        primary: { contrastColor: 'var(--color-primary-contrast)' },
        surface: { 0: '#ffffff', ...ramp('neutral') },
        focusRing: { shadow: 'none' }, // The outline above replaces Lara's halo.
        // Keep selected option text readable for pale brands.
        highlight: {
          background: '{app.selected}',
          focusBackground: '{app.selectedHover}',
          color: '{app.content}',
          focusColor: '{app.content}',
        },
        formField: {
          disabledColor: '{surface.400}',
          borderColor: '{app.borderStrong}',
          // The runtime theme raises pale focus colours to 3:1 contrast.
          hoverBorderColor: 'var(--color-accent)',
          focusBorderColor: 'var(--color-accent)',
        },
        app: {
          // Slightly warm page with white cards.
          canvas: 'color-mix(in oklab, var(--color-neutral-50), #f8f0de 10%)',
          card: '#ffffff',
          sunken: 'var(--color-neutral-100)',
          // Tenant tint between primary 50 and 100.
          selected: 'color-mix(in oklab, var(--color-primary-50), var(--color-primary-100))',
          // Stronger tint when hovering a selected item.
          selectedHover: 'var(--color-primary-100)',
          border: 'var(--color-neutral-200)',
          borderStrong: 'var(--color-neutral-300)',
          content: 'var(--color-neutral-900)',
          contentMuted: 'var(--color-neutral-700)', // 600 clears AA on light surfaces
          contentDisabled: 'var(--color-neutral-500)',
        },
      },
      dark: {
        surface: { 0: '#ffffff', ...ramp('neutral') },
        focusRing: { shadow: 'none' }, // The outline above replaces Lara's halo.
        highlight: {
          background: '{app.selected}',
          focusBackground: '{app.selectedHover}',
          color: '{app.content}',
          focusColor: '{app.content}',
        },
        formField: {
          disabledColor: '{surface.600}',
          borderColor: '{app.borderStrong}',
          hoverBorderColor: 'var(--color-accent)',
          focusBorderColor: 'var(--color-accent)',
        },
        app: {
          // Warm dark page with lighter cards.
          canvas: 'color-mix(in oklab, var(--color-neutral-950), #2a2009 10%)',
          card: 'var(--color-neutral-900)',
          sunken: 'var(--color-neutral-800)',
          // Tenant tint between primary 900 and 950.
          selected: 'color-mix(in oklab, var(--color-primary-900), var(--color-primary-950))',
          // Stronger tint when hovering a selected item.
          selectedHover: 'var(--color-primary-900)',
          border: 'var(--color-neutral-800)',
          borderStrong: 'var(--color-neutral-700)',
          content: 'var(--color-neutral-100)',
          contentMuted: 'var(--color-neutral-300)',
          contentDisabled: 'var(--color-neutral-500)',
        },
      },
    },
  },
  components: {
    button: {
      // Buttons need less padding than fields.
      root: {
        borderRadius: '{borderRadius.full}',
        paddingX: space(4),
        paddingY: space(2),
        iconOnlyWidth: space(10),
        sm: { paddingX: space(3), paddingY: space(1.5), iconOnlyWidth: space(9) },
        lg: { paddingX: space(5), paddingY: space(2), iconOnlyWidth: space(11) },
      },
      colorScheme: {
        // Keep pale brand links and outlined button hovers visible.
        light: {
          root: {
            // Filled red for the final confirm step in a dialog. 600, not 500, so white clears AA.
            danger: {
              background: '{red.600}',
              hoverBackground: '{red.700}',
              activeBackground: '{red.800}',
              borderColor: '{red.600}',
              hoverBorderColor: '{red.700}',
              activeBorderColor: '{red.800}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
            },
            // The hover and active fills are darker, so they need their own text colour.
            primary: {
              hoverColor: 'var(--color-primary-contrast-600)',
              activeColor: 'var(--color-primary-contrast-700)',
            },
          },
          text: {
            primary: { color: 'var(--color-link)', hoverBackground: '{app.selected}' },
            // Lara hovers to surface.100, which vanishes on the grey dialog footer.
            secondary: { hoverBackground: '{surface.200}', activeBackground: '{surface.300}' },
          },
          link: { color: 'var(--color-link)', hoverColor: 'var(--color-link)' },
          outlined: {
            primary: {
              color: 'var(--color-link)',
              borderColor: 'var(--color-link)',
              hoverBackground: '{app.selected}',
            },
            secondary: {
              borderColor: '{app.borderStrong}',
              hoverBackground: 'var(--color-neutral-200)',
            },
            text: {
              hoverBackground: '{app.selected}',
            },
          },
        },
        dark: {
          text: { primary: { color: 'var(--color-link)', hoverBackground: '{app.selected}' } },
          link: { color: 'var(--color-link)', hoverColor: 'var(--color-link)' },
          outlined: {
            primary: {
              color: 'var(--color-link)',
              borderColor: 'var(--color-link)',
              hoverBackground: '{primary.900}',
            },
            secondary: {
              borderColor: '{app.borderStrong}',
              hoverBackground: 'var(--color-neutral-800)',
            },
            text: {
              hoverBackground: '{app.selected}',
            },
          },
        },
      },
      // Shrink pressed buttons. Make disabled filled buttons gray, except danger.
      css: () => `
        .p-button { transition: background-color .15s, border-color .15s, color .15s, box-shadow .15s, transform .12s var(--ease-bounce); }
        /* Lara gives every severity its own halo, so the semantic ring cannot reach buttons. */
        .p-button:focus-visible {
          outline: var(--p-focus-ring-width) var(--p-focus-ring-style) var(--color-focus-ring);
          outline-offset: var(--p-focus-ring-offset);
          box-shadow: none;
        }
        .p-button:not(:disabled):active { transform: scale(0.96); }
        @media (prefers-reduced-motion: reduce) { .p-button:active { transform: none; } }
        .p-button:disabled:not(.p-button-text):not(.p-button-outlined):not(.p-button-link):not(.p-button-danger) {
          background-color: var(--color-neutral-300);
          border-color: var(--color-neutral-300);
          color: var(--color-neutral-500);
          cursor: not-allowed;
        }
      `,
    },
    card: {
      root: { background: '{app.card}', shadow: 'none' }, // flat: bordered, no shadow
      title: { fontSize: 'var(--text-lg)', fontWeight: '600' }, // card-title: text-lg font-semibold
      css: () => `.p-card { border: 1px solid var(--color-border-strong); }`,
    },
    checkbox: {
      colorScheme: { light: { icon: { checkedHoverColor: 'var(--color-primary-contrast-600)' } } },
      // Pop the box when checked.
      css: () => `
        .p-checkbox-box { transition: background-color .15s, border-color .15s, transform .15s var(--ease-bounce); }
        .p-checkbox-checked .p-checkbox-box { animation: ds-check-pop .25s var(--ease-bounce); }
        @keyframes ds-check-pop { from { transform: scale(0.7); } 60% { transform: scale(1.12); } to { transform: scale(1); } }
        @media (prefers-reduced-motion: reduce) { .p-checkbox-checked .p-checkbox-box { animation: none; } }
      `,
    },
    radiobutton: {
      colorScheme: { light: { icon: { checkedHoverColor: 'var(--color-primary-contrast-600)' } } },
    },
    avatar: {
      root: { fontSize: 'var(--text-sm)' },
      lg: { fontSize: 'var(--text-xl)' },
      xl: { fontSize: 'var(--text-2xl)' },
      css: () => `.p-avatar { font-weight: var(--font-weight-semibold); line-height: 1; }`, // line-height:1 centers initials
    },
    dialog: { title: { fontSize: 'var(--text-xl)' }, header: { padding: `${space(4)} ${space(6)}` } },
    drawer: {
      title: { fontSize: 'var(--text-2xl)' },
      // Slide drawers from their docked edge.
      css: () => `
        .p-drawer-enter-active, .p-drawer-leave-active { transition: transform .3s cubic-bezier(.4, 0, .2, 1), opacity .3s; }
        .p-drawer-left .p-drawer-enter-from, .p-drawer-left .p-drawer-leave-to { transform: translateX(-100%); }
        .p-drawer-right .p-drawer-enter-from, .p-drawer-right .p-drawer-leave-to { transform: translateX(100%); }
        .p-drawer-top .p-drawer-enter-from, .p-drawer-top .p-drawer-leave-to { transform: translateY(-100%); }
        .p-drawer-bottom .p-drawer-enter-from, .p-drawer-bottom .p-drawer-leave-to { transform: translateY(100%); }
        .p-drawer-full .p-drawer-enter-from, .p-drawer-full .p-drawer-leave-to { opacity: 0; }
        @media (prefers-reduced-motion: reduce) { .p-drawer-enter-active, .p-drawer-leave-active { transition: none; } }
      `,
    },
    tooltip: {
      // Wrap only when the tooltip reaches its maximum width.
      css: () => `
        .p-tooltip { width: max-content; }
        .p-tooltip-image {
          --p-tooltip-max-width: min(500px, calc(100vw - 2rem));
          --p-tooltip-background: var(--color-card);
          --p-tooltip-color: var(--color-content);
        }
      `,
    },
    message: {
      text: {
        fontSize: 'var(--text-base)',
        sm: { fontSize: 'var(--text-sm)' },
        lg: { fontSize: 'var(--text-lg)' },
      },
      css: () => `
        .p-message-tip { background: var(--color-selected); color: var(--color-content-muted); }
        .p-message-tip .p-message-content { align-items: flex-start; }
        .p-message-tip .p-message-content > :first-child { margin-top: calc(var(--spacing) * 0.5); }
        .p-message-tip .p-message-text { color: var(--color-content); }
      `,
    },
    // A white card with a coloured left bar and icon. Lara tints the whole toast and colours the
    // text with the severity, which reads as a shout and leaves text-on-tint short of AA contrast.
    toast: {
      root: { width: '24rem', borderRadius: '{borderRadius.sm}', borderWidth: '0 0 0 4px' },
      // The app fires a whole sentence as the summary, so it is body copy, not a title.
      summary: { fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-normal)' },
      detail: { fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-normal)' },
      colorScheme: {
        light: toastSeverities('light'),
        dark: toastSeverities('dark'),
      },
      // `toast.<severity>.color` paints the icon and the text together, so restore the colour the
      // icon lost when the text went neutral.
      css: () =>
        TOAST_SEVERITIES.map(
          ({ severity, color }) => `.p-toast-message-${severity} .p-toast-message-icon { color: ${color}; }`,
        ).join('\n'),
    },
    selectbutton: {
      root: { borderRadius: '{borderRadius.full}' },
      // Lara skips hover styles on checked options.
      css: () => `
        .p-selectbutton .p-togglebutton-checked:hover { background: var(--color-selected-hover); }
      `,
    },
    togglebutton: {
      root: {
        padding: space(1),
        gap: space(2),
        fontWeight: 'var(--font-weight-medium)',
      },
      content: {
        padding: `${space(1)} ${space(3)}`,
      },
      colorScheme: {
        light: {
          root: {
            background: 'transparent',
            color: '{app.contentMuted}',
            checkedColor: '{app.content}',
            hoverBackground: '{app.selected}',
            checkedBackground: '{app.selected}',
          },
          icon: { color: '{surface.500}', hoverColor: '{surface.700}', checkedColor: '{surface.900}' },
        },
        dark: {
          root: {
            background: 'transparent',
            color: '{app.contentMuted}',
            checkedColor: '{app.content}',
            hoverBackground: '{app.selected}',
            checkedBackground: '{app.selected}',
          },
          icon: { color: '{surface.400}', hoverColor: '{surface.300}', checkedColor: '{surface.0}' },
        },
      },
    },
    // Flat tabs with a bottom line.
    tabs: {
      tablist: { background: 'transparent', borderColor: '{app.border}', borderWidth: '0 0 1px 0' },
      tab: {
        background: 'transparent',
        borderColor: 'transparent',
        hoverBorderColor: '{app.borderStrong}',
        // Visible tenant colour for pale brands.
        activeBorderColor: 'var(--color-accent)',
        borderWidth: '0 0 2px 0',
        margin: '0 0 -1px 0', // Cover the tab list line.
        color: '{app.contentMuted}',
        hoverColor: '{app.content}',
        activeColor: '{app.content}',
        fontWeight: 'var(--font-weight-medium)',
        padding: `${space(3)} ${space(4)}`,
        gap: space(2),
      },
      // The panel sits on the page, its content aligned with the tab bar.
      tabpanel: { background: 'transparent', padding: `${space(4)} 0 0` },
      colorScheme: {
        light: {
          tab: {
            background: 'transparent',
            hoverBackground: '{app.selected}',
            hoverBorderColor: '{app.selected}',
            activeBackground: 'transparent',
          },
        },
        dark: {
          tab: {
            background: 'transparent',
            hoverBackground: '{app.selected}',
            hoverBorderColor: '{app.selected}',
            activeBackground: 'transparent',
          },
        },
      },
      css: () => `
        .p-tablist-tab-list > .p-tab:first-child { padding-inline-start: 0; } /* Align with the content. */
        .p-tab-active:hover { background: var(--color-selected); }
      `,
    },
    paginator: {
      root: { padding: `${space(2)} ${space(3)}`, background: '{app.card}' },
      navButton: { selectedBackground: '{app.selected}', selectedColor: '{app.content}' }, // Neutral active page.
      // Use a soft fill for the rows menu.
      css: () => `
        .p-paginator-rpp-dropdown {
          border-color: transparent;
          background: var(--color-sunken);
          box-shadow: none;
        }
        .p-paginator-rpp-dropdown:not(.p-disabled):hover {
          background: color-mix(in oklab, var(--color-sunken), var(--color-content) 8%);
        }
      `,
    },
    datatable: {
      headerCell: { padding: `${space(3)} ${space(5)}` },
      columnTitle: { fontWeight: 'var(--font-weight-medium)' },
      bodyCell: { padding: `${space(3)} ${space(5)}` },
      footerCell: { padding: `${space(3)} ${space(5)}` },
      paginatorBottom: { borderWidth: '1px 0 0 0' }, // Put the divider above the paginator.
      colorScheme: {
        light: {
          headerCell: { background: '{app.card}', color: '{app.contentMuted}' },
          row: { background: '{app.card}', hoverBackground: '{primary.50}', hoverColor: '{app.content}' },
        },
        dark: {
          headerCell: { background: '{app.card}', color: '{app.contentMuted}' },
          row: { background: '{app.card}', hoverBackground: '{primary.950}', hoverColor: '{app.content}' },
        },
      },
      css: () => `
        .p-datatable { border: 1px solid var(--color-border-strong); }

        .p-datatable-thead > tr > th,
        .p-datatable-tbody > tr > td,
        .p-datatable-tfoot > tr > td {
          height: 3.5rem; /* Equal row height. */
          vertical-align: middle; /* Bulma's reset tops them. */
        }

        /* A number column sets headerClass and bodyClass to text-right; this moves its title too. */
        .p-datatable-thead > tr > th.text-right .p-datatable-column-header-content {
          justify-content: flex-end;
        }

        .p-datatable-thead > tr > th.text-center .p-datatable-column-header-content {
          justify-content: center;
        }

        /* Line the group chevron up with its label. With its margin it is space(8) wide, so a
           grouped row indents pl-8 to line up with its group. */
        .p-datatable-row-group-header .p-datatable-row-toggle-button {
          vertical-align: middle;
          width: ${space(6)};
          height: ${space(6)};
          margin-inline-end: ${space(2)};
        }

        /* Remove the last divider from the rounded card edge. */
        .p-datatable-tbody:last-child > tr:last-child > td,
        .p-datatable-tfoot > tr:last-child > td,
        .p-datatable-tfoot > tr:last-child > th {
          border-bottom-width: 0;
        }
      `,
    },
    popover: { content: { padding: '0' } },
    tag: {
      root: { fontSize: 'var(--text-sm)', borderRadius: '{border.radius.full}' },
      colorScheme: {
        light: {
          success: { background: '{green.50}', color: '{green.700}' },
          info: { background: '{sky.50}', color: '{sky.700}' },
          danger: { background: '{red.50}', color: '{red.700}' },
          warn: { background: '{orange.50}', color: '{orange.700}' },
        },
        dark: {
          success: { background: '{green.950}', color: '{green.300}' },
          info: { background: '{sky.950}', color: '{sky.300}' },
          danger: { background: '{red.950}', color: '{red.300}' },
          warn: { background: '{orange.950}', color: '{orange.300}' },
        },
      },
    },
    progressbar: { label: { fontSize: 'var(--text-sm)' } },
    floatlabel: { root: { active: { fontSize: 'var(--text-xs)' } } },
    iftalabel: { root: { fontSize: 'var(--text-xs)' } },
  },
});
